// src/components/ProdMgrs/utils/pmgrCommissionMath.js
// =============================================================================
// FILE: pmgrCommissionMath.js
// DOMAIN: Program Manager Commissions (PMGR)
// PURPOSE:
//   Pure calculation engine for PMGR commission math.
//   - No React. No UI logic.
//   - Deterministic & easy to unit test.
//   - Emits BOTH:
//       (A) PMGR-friendly computed fields, and
//       (B) legacy CommissionsRow-compatible fields so shared UI renders correctly.
//
// INPUTS:
//   rows            → line rows from backend (PMGR “programs” / line detail)
//   configRowsOrMap → either:
//                      - config rows array from /api/prod-mgrs/quota-breakdown
//                      - OR a pre-built lookup map { [CATEGORY]: row }
//
// OUTPUT (per line row):
//   Adds computed values (per Category bucket):
//     - running net sales (cumulative per category)
//     - line commission dollars
//     - running commission dollars (cumulative per category)
//     - tier triggered for that line (Tier1/Tier2/Tier3)
//
//   IMPORTANT: shared legacy UI components often expect these keys:
//     - Category
//     - RunningNetTotal
//     - LineCommissions
//     - RunningCommissionsTotal
//     - TriggeredTiers
// =============================================================================

// =============================================================================
// BLOCK PMGR.MATH.0 — Helper to build config lookup by CATEGORY
// PURPOSE:
//   Transform quota/config rows into a plain object map keyed by CATEGORY.
// NOTES:
//   - We use a plain object (not Map) to keep usage simple and JSON-friendly.
//   - Config rows come from pmgr_comm and are expected to contain:
//       CATEGORY, Base, T_PCI, D_PCI, COMM1, COMM2, COMM3 (and optionally Tier*Rate aliases)
// =============================================================================
export function buildConfigMap(configRows) {
  const map = {};
  (Array.isArray(configRows) ? configRows : []).forEach((row) => {
    const key = String(row?.CATEGORY ?? '').trim();
    if (key) {
      map[key] = row;
    }
  });
  return map;
}

// =============================================================================
// BLOCK PMGR.MATH.1 — Main calculator
// PURPOSE:
//   Compute per-line commissions using a tiered rate determined by the running net
//   total within each Product Category (PMGR category).
//
// TIER LOGIC (per category-month bucket):
//   - base = Base
//   - goal = Base + (T_PCI * D_PCI)
//   - line commission is computed as delta of cumulative tiered commission:
//       line = cumulative(nextNet) - cumulative(prevNet)
//   - this matches commissions SQL behavior when a single line crosses one or
//     both tier boundaries in the same row.
// =============================================================================
export function computeProgramManagerCommissions(rows, configRowsOrMap) {
  // Allow passing either configRows array OR the prebuilt map
  const configMap = Array.isArray(configRowsOrMap)
    ? buildConfigMap(configRowsOrMap)
    : configRowsOrMap || {};

  // ---------------------------------------------------------------------------
  // BLOCK PMGR.MATH.1A — Safe numeric coercion (handles "$", ",", blanks)
  // Why:
  //  - Postgres NUMERIC often arrives as string
  //  - Strings may include formatting (commas) depending on upstream
  //  - If Base parses as NaN -> treated as 0 -> tiers flip too early
  // ---------------------------------------------------------------------------
  function toNum(v) {
    if (v === null || v === undefined) {
      return 0;
    }
    if (typeof v === 'number') {
      return Number.isFinite(v) ? v : 0;
    }
    if (typeof v === 'string') {
      const cleaned = v.replace(/[$,]/g, '').trim();
      if (!cleaned) {
        return 0;
      }
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  // Running totals tracked per category + month (reset each month)
  // Key format: `${category}__${YYYY-MM}`
  const runningNetByBucket = {};
  const runningCommByBucket = {};

  function bucketKey(cat, isoDate) {
    const s = typeof isoDate === 'string' ? isoDate.trim() : '';
    const ymd = s.slice(0, 10); // 'YYYY-MM-DD'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      return `${cat}__unknown`;
    }
    const ym = ymd.slice(0, 7); // 'YYYY-MM'
    return `${cat}__${ym}`;
  }

  function formatPercent(rate) {
    return `${(toNum(rate) * 100).toFixed(2)}%`;
  }

  // Mirrors the cumulative tier math from regular commissions SQL.
  // We compute line commission as delta(cumulative at nextNet - cumulative at prevNet)
  // so a single line can span Tier1/Tier2/Tier3 boundaries correctly.
  function cumulativeCommissionAt(netTotal, tier2Start, tier3Start, r1, r2, r3) {
    const runningNet = toNum(netTotal);
    const t2 = toNum(tier2Start);
    const t3 = toNum(tier3Start);

    const tier1Amount = Math.min(t2, runningNet) * r1;
    const tier2Amount = Math.max(Math.min(t3 - t2, runningNet - t2), 0) * r2;
    const tier3Amount = Math.max(runningNet - t3, 0) * r3;

    return tier1Amount + tier2Amount + tier3Amount;
  }

  function triggeredTierDisplay(nextNet, net, tier2Start, tier3Start, r1, r2, r3) {
    const prevNet = toNum(nextNet) - toNum(net);
    const t2 = toNum(tier2Start);
    const t3 = toNum(tier3Start);

    if (nextNet <= t2) {
      return formatPercent(r1);
    }

    if (nextNet >= t2 && nextNet <= t3) {
      if (prevNet > t2) {
        return formatPercent(r2);
      }
      return `${formatPercent(r1)}/${formatPercent(r2)}`;
    }

    if (prevNet > t3) {
      return formatPercent(r3);
    }
    if (prevNet < t2) {
      return `${formatPercent(r1)}/${formatPercent(r2)}/${formatPercent(r3)}`;
    }
    return `${formatPercent(r2)}/${formatPercent(r3)}`;
  }

  return (Array.isArray(rows) ? rows : []).map((row) => {
    // PMGR lines typically come back with ProductGroup (pg.category)
    // Fallbacks included to avoid brittle coupling.
    const cat = String(row?.ProductGroup ?? row?.Category ?? row?.CATEGORY ?? '').trim();
    if (!cat) {
      return row;
    }

    const cfg = configMap[cat];
    if (!cfg) {
      // Still return Category so shared UI can show something reasonable.
      return { ...row, Category: row?.Category ?? cat };
    }

    const bkey = bucketKey(cat, row?.InvoiceDate);

    // Initialize per-bucket running totals
    if (runningNetByBucket[bkey] === undefined) {
      runningNetByBucket[bkey] = 0;
      runningCommByBucket[bkey] = 0;
    }

    const prevNet = toNum(runningNetByBucket[bkey]);

    // Line-level net sales input (supports multiple possible names)
    const net = toNum(row?.NetSale ?? row?.NetSales ?? 0);

    // Tier thresholds from config
    const base = toNum(cfg?.Base ?? 0);
    const tPci = toNum(cfg?.T_PCI ?? 0);
    const dPci = toNum(cfg?.D_PCI ?? 0);
    const dSamp = toNum(cfg?.D_Samp ?? 0);
    const repMult = toNum(cfg?.Rep_Mult ?? 0);

    const computedMoBase = (base + repMult - dSamp * tPci) / 12;
    const computedMoGoal = (base + repMult + tPci * dPci - dSamp * tPci) / 12;

    // Prefer quota endpoint monthly thresholds when present.
    const tier2StartRaw = cfg?.moBase ?? cfg?.Tier2Start;
    const tier3StartRaw = cfg?.moGoal ?? cfg?.Tier3Start;

    const tier2Start =
      tier2StartRaw === null || tier2StartRaw === undefined ? computedMoBase : toNum(tier2StartRaw);
    const tier3Start =
      tier3StartRaw === null || tier3StartRaw === undefined ? computedMoGoal : toNum(tier3StartRaw);

    // Tier rates from config (supports both canonical and alias naming)
    const r1 = toNum(cfg?.COMM1 ?? cfg?.Tier1Rate ?? 0);
    const r2 = toNum(cfg?.COMM2 ?? cfg?.Tier2Rate ?? 0);
    const r3 = toNum(cfg?.COMM3 ?? cfg?.Tier3Rate ?? 0);

    // Update running totals for this bucket (category-month)
    const nextNet = prevNet + net;

    // Commission dollars for this line using cumulative tier math.
    const prevMonthlyCommission = cumulativeCommissionAt(
      prevNet,
      tier2Start,
      tier3Start,
      r1,
      r2,
      r3
    );
    const nextMonthlyCommission = cumulativeCommissionAt(
      nextNet,
      tier2Start,
      tier3Start,
      r1,
      r2,
      r3
    );
    const lineCommission = nextMonthlyCommission - prevMonthlyCommission;

    const nextComm = toNum(runningCommByBucket[bkey]) + lineCommission;

    // What shared CommissionsRow expects to DISPLAY in the triggered tiers cell.
    const triggeredTiersDisplay = triggeredTierDisplay(
      nextNet,
      net,
      tier2Start,
      tier3Start,
      r1,
      r2,
      r3
    );

    // Effective rate for this line (useful for sorting when a line spans tiers).
    const effectiveRate = net !== 0 ? lineCommission / net : 0;

    let tierName = 'Tier1';
    if (nextNet <= tier2Start) {
      tierName = 'Tier1';
    } else if (nextNet <= tier3Start) {
      tierName = prevNet > tier2Start ? 'Tier2' : 'Tier1/Tier2';
    } else if (prevNet > tier3Start) {
      tierName = 'Tier3';
    } else if (prevNet < tier2Start) {
      tierName = 'Tier1/Tier2/Tier3';
    } else {
      tierName = 'Tier2/Tier3';
    }

    runningNetByBucket[bkey] = nextNet;
    runningCommByBucket[bkey] = nextComm;

    return {
      ...row,

      // ----------------------------------------------------------------------
      // Canonical category key(s)
      // ----------------------------------------------------------------------
      Category: cat, // ✅ shared/legacy UI generally expects this
      ProductGroup: cat, // keep PMGR category explicitly present too

      // ----------------------------------------------------------------------
      // Legacy/shared commissions table fields (CommissionsRow compatibility)
      // ----------------------------------------------------------------------
      RunningNetTotal: nextNet,
      LineCommissions: lineCommission,
      RunningCommissionsTotal: nextComm,
      // Shared UI display field (must be a % string, not "Tier1")
      TriggeredTiers: triggeredTiersDisplay,

      // Keep the tier label separately if you want it for debugging or tooltips
      TriggeredTierName: tierName,

      // Also expose the numeric rate explicitly (safe for any future math)
      CommissionRate: effectiveRate,

      // ----------------------------------------------------------------------
      // PMGR-friendly names (safe for PMGR-specific components)
      // ----------------------------------------------------------------------
      runningNet: nextNet,
      lineCommission,
      runningCommission: nextComm,

      // ----------------------------------------------------------------------
      // Tier metadata (useful for formatting/shading elsewhere)
      // ----------------------------------------------------------------------
      Tier1Rate: r1,
      Tier2Rate: r2,
      Tier3Rate: r3,
      Tier2Start: tier2Start,
      Tier3Start: tier3Start,
    };
  });
}
// =============================================================================
// BLOCK PMGR.MATH.EXPORTS.0 — Public exports (compat aliases)
// PURPOSE:
//   Keep canonical names (buildConfigMap, computeProgramManagerCommissions)
//   AND export the feature names used by ProdMgrs tables to avoid churn.
// =============================================================================
export const buildPmgrConfigMap = buildConfigMap;
export const computePmgrLineCalcs = computeProgramManagerCommissions;
